import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { RootState } from '../../store';
import { useAppSelector } from '../../store/hooks';
import { selectCurrentApplication } from '../../store/slices/application.slice';
import traceApi from '../../services/api/trace.api';
import applicationApi from '../../services/api/application.api';
import MainLayout from '../../components/layout/MainLayout';
import ApplicationRequired from '../../components/common/ApplicationRequired';

interface Application {
  id: string;
  name: string;
  description: string;
  language: string;
  framework?: string;
  environment: string;
  api_key: string;
  status: string;
  created_at: string;
}

interface VerificationStatus {
  checking: boolean;
  success: boolean;
  message: string;
  lastCheck?: Date;
  tracesReceived?: number;
}

type Language = 'nodejs' | 'python' | 'java' | 'go' | 'dotnet' | 'php' | 'ruby';
type CopiedField = 'apiKey' | 'installCommand' | null;
type NodeJSSetupType = 'express-basic' | 'express-prisma-auto' | 'express-prisma-manual' | 'express-typeorm' | 'nestjs';
type PythonSetupType = 'flask' | 'django' | 'fastapi';
type PHPSetupType = 'laravel' | 'symfony' | 'wordpress' | 'php-pure';

export default function APMAgentInstallPage() {
  const { t } = useTranslation('agents');
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const globalSelectedAppId = useAppSelector(selectCurrentApplication);
  const [currentStep, setCurrentStep] = useState(1);
  const [existingApplications, setExistingApplications] = useState<Application[]>([]);
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);
  const [loadingApplications, setLoadingApplications] = useState(true);
  const [selectedLanguage, setSelectedLanguage] = useState<Language>('nodejs');
  const [nodejsSetupType, setNodejsSetupType] = useState<NodeJSSetupType>('express-prisma-manual');
  const [pythonSetupType, setPythonSetupType] = useState<PythonSetupType>('flask');
  const [phpSetupType, setPhpSetupType] = useState<PHPSetupType>('laravel');
  const [apiKey, setApiKey] = useState<string>('');
  const [serviceName, setServiceName] = useState<string>('my-service');
  const [generatingKey, setGeneratingKey] = useState(false);
  const [copiedField, setCopiedField] = useState<CopiedField>(null);
  const [verification, setVerification] = useState<VerificationStatus>({
    checking: false,
    success: false,
    message: t('install.verification.noData'),
  });

  const isAllAppsSelected = !globalSelectedAppId || globalSelectedAppId === 'all';

  useEffect(() => {
    loadExistingApplications();
  }, []);

  // Monitor global application changes
  useEffect(() => {
    if (isAllAppsSelected && selectedApplication) {
      // User switched to "All Applications" - show warning
      toast.error(
        t('errors.cannotUseAllApps') || 'Cannot configure APM agent with "All Applications" selected. Please select a specific application.',
        {
          duration: 6000,
          icon: '⚠️',
        }
      );
    } else if (globalSelectedAppId && globalSelectedAppId !== selectedApplication?.id) {
      // User switched to a different specific application
      const newApp = existingApplications.find(app => app.id === globalSelectedAppId);
      if (newApp) {
        handleSelectApplication(newApp);
        toast.success(
          t('info.applicationSwitched') || `Switched to application: ${newApp.name}`,
          {
            duration: 3000,
            icon: '🔄',
          }
        );
      }
    }
  }, [globalSelectedAppId]);

  const loadExistingApplications = async () => {
    try {
      const response = await applicationApi.getAll();
      if (response.success && response.data) {
        let activeApps = response.data.filter((app) => app.status === 'active');

        // If user has selected a specific app in header, show ONLY that application
        if (globalSelectedAppId && globalSelectedAppId !== 'all') {
          activeApps = activeApps.filter(app => app.id === globalSelectedAppId);
        }

        setExistingApplications(activeApps);

        // If we have exactly one app (from filter above), auto-select it
        if (activeApps.length === 1) {
          handleSelectApplication(activeApps[0]);
        }
      }
    } catch (error) {
      console.error('Failed to load applications:', error);
    } finally {
      setLoadingApplications(false);
    }
  };

  const handleSelectApplication = (app: Application) => {
    setSelectedApplication(app);
    setApiKey(app.api_key);
    setServiceName(app.name);
    // Detect language from app if possible
    if (app.language && ['nodejs', 'python', 'java', 'go', 'dotnet', 'php', 'ruby'].includes(app.language)) {
      setSelectedLanguage(app.language as Language);
    }
  };

  const handleUseSelectedKey = () => {
    if (!selectedApplication || !apiKey) {
      toast.error(t('install.validation.mustSelectApp') || 'Please select an application', {
        duration: 4000,
        icon: '⚠️',
      });
      return;
    }
    setCurrentStep(2);
  };

  const generateApiKey = async () => {
    setGeneratingKey(true);
    try {
      const languageMap: Record<Language, { name: string; framework?: string }> = {
        nodejs: { name: 'Node.js', framework: 'express' },
        python: { name: 'Python', framework: 'flask' },
        java: { name: 'Java', framework: 'spring' },
        go: { name: 'Go' },
        dotnet: { name: '.NET' },
        php: { name: 'PHP', framework: 'laravel' },
        ruby: { name: 'Ruby', framework: 'rails' },
      };

      const timestamp = new Date().toISOString().split('T')[0];
      const langInfo = languageMap[selectedLanguage];

      const response = await applicationApi.create({
        name: `APM - ${langInfo.name} - ${serviceName} - ${timestamp}`,
        description: `Application for APM tracing in ${langInfo.name}`,
        language: selectedLanguage,
        framework: langInfo.framework,
        environment: 'production',
      });

      if (response.success && response.data) {
        setApiKey(response.data.api_key);
        setSelectedApplication(response.data);
        setCurrentStep(2);
      } else {
        throw new Error(t('install.apiKey.generating'));
      }
    } catch (error: any) {
      console.error('Failed to generate API key:', error);
      toast.error(error.response?.data?.error || t('install.apiKey.generating') || 'Failed to generate API key', {
        duration: 5000,
        icon: '❌',
      });
    } finally {
      setGeneratingKey(false);
    }
  };

  const verifyConnection = async () => {
    setVerification({ checking: true, success: false, message: t('install.verification.checking') });

    try {
      // Add minimum 2 second delay + actual API call
      const [, response] = await Promise.all([
        new Promise(resolve => setTimeout(resolve, 2000)),
        (async () => {
          const endTime = Date.now();
          const startTime = endTime - 5 * 60 * 1000; // Last 5 minutes
          // Use the selected application ID, not 'any'
          const appId = selectedApplication?.id || 'any';
          return await traceApi.getAll(appId, { startTime, endTime, limit: 10 });
        })()
      ]);

      console.log('Trace verification response:', response);
      console.log('Using application ID:', selectedApplication?.id);

      if (response.success && response.data && response.data.length > 0) {
        setVerification({
          checking: false,
          success: true,
          message: `${t('install.verification.success')}! Found ${response.data.length} recent trace(s).`,
          lastCheck: new Date(),
          tracesReceived: response.data.length,
        });
        setCurrentStep(4);
      } else {
        setVerification({
          checking: false,
          success: false,
          message: t('apm.verification.noTraces'),
          lastCheck: new Date(),
        });
      }
    } catch (error: any) {
      console.error('Trace verification error:', error);
      setVerification({
        checking: false,
        success: false,
        message: `${t('install.verification.notFound')}: ${error.message || t('install.verification.verifying')}`,
        lastCheck: new Date(),
      });
    }
  };

  const copyToClipboard = async (text: string, field: CopiedField) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const getNodeJSInstrumentation = () => {
    const baseUrl = 'https://api-monitoring.devskin.com';

    switch (nodejsSetupType) {
      case 'express-basic':
        return `npm install @devskin/agent

// File: index.js or server.js
const { init, startAgent, expressMiddleware } = require('@devskin/agent');

const agent = init({
  serverUrl: '${baseUrl}',
  apiKey: '${apiKey}',
  applicationId: '${selectedApplication?.id}',
  serviceName: '${serviceName}',
  environment: 'production',
  instrumentDatabase: true
});

startAgent();

const express = require('express');
const app = express();

app.use(expressMiddleware(agent));

// Your routes here
app.get('/users', async (req, res) => {
  // Automatically traced
});

app.listen(3000);`;

      case 'express-prisma-auto':
        return `npm install @devskin/agent

// File: index.js or server.js - Initialize agent FIRST
const { init, startAgent, expressMiddleware } = require('@devskin/agent');

const agent = init({
  serverUrl: '${baseUrl}',
  apiKey: '${apiKey}',
  applicationId: '${selectedApplication?.id}',
  serviceName: '${serviceName}',
  environment: 'production',
  instrumentDatabase: true  // Automatically instruments Prisma
});

startAgent();

// Now import Prisma - already instrumented automatically
const { PrismaClient } = require('@prisma/client');
const prisma = new PrismaClient();

const express = require('express');
const app = express();
app.use(expressMiddleware(agent));

app.get('/users', async (req, res) => {
  const users = await prisma.user.findMany();
  res.json(users);
});

app.listen(3000);`;

      case 'express-prisma-manual':
        return `npm install @devskin/agent

// File: index.js or server.js
const { init, startAgent, expressMiddleware, getAgent, SpanBuilder } = require('@devskin/agent');

const agent = init({
  serverUrl: '${baseUrl}',
  apiKey: '${apiKey}',
  applicationId: '${selectedApplication?.id}',
  serviceName: '${serviceName}',
  environment: 'production'
});

startAgent();

const { PrismaClient } = require('@prisma/client');
const prisma = new PrismaClient();

// Manual middleware for more control
prisma.$use(async (params, next) => {
  const agent = getAgent();
  const config = agent.getConfig();

  const span = new SpanBuilder(
    \`\${params.action} \${params.model || 'query'}\`,
    'CLIENT',
    config.serviceName,
    config.serviceVersion,
    config.environment,
    agent
  );

  span.setAttribute('db.system', 'prisma');
  span.setAttribute('db.operation', params.action);
  if (params.model) span.setAttribute('db.sql.table', params.model);

  try {
    const result = await next(params);
    span.setStatus('OK');
    if (Array.isArray(result)) {
      span.setAttribute('db.rows_affected', result.length);
    }
    return result;
  } catch (error) {
    span.setStatus('ERROR');
    span.recordError(error);
    throw error;
  } finally {
    span.end();
  }
});

const express = require('express');
const app = express();
app.use(expressMiddleware(agent));

app.get('/users', async (req, res) => {
  const users = await prisma.user.findMany();
  res.json(users);
});

app.listen(3000);`;

      case 'express-typeorm':
        return `npm install @devskin/agent

// File: index.js or server.js - BEFORE TypeORM import
const { init, startAgent, expressMiddleware } = require('@devskin/agent');

const agent = init({
  serverUrl: '${baseUrl}',
  apiKey: '${apiKey}',
  applicationId: '${selectedApplication?.id}',
  serviceName: '${serviceName}',
  environment: 'production',
  instrumentDatabase: true
});

startAgent();

// Now import and configure TypeORM
const { DataSource } = require('typeorm');

const AppDataSource = new DataSource({
  type: 'mysql',
  host: 'localhost',
  port: 3306,
  username: 'root',
  password: 'password',
  database: 'mydb',
  entities: ['./entities/*.js']
});

await AppDataSource.initialize();

// Configure Express
const express = require('express');
const app = express();

app.use(expressMiddleware(agent));

// Your routes - TypeORM queries are automatically traced
app.get('/users', async (req, res) => {
  const users = await AppDataSource.getRepository(User).find();
  res.json(users);
});

app.listen(3000);`;

      case 'nestjs':
        return `npm install @devskin/agent

// File: main.ts - BEFORE NestFactory.create()
import { init, startAgent } from '@devskin/agent';

const agent = init({
  serverUrl: '${baseUrl}',
  apiKey: '${apiKey}',
  applicationId: '${selectedApplication?.id}',
  serviceName: '${serviceName}',
  environment: 'production',
  instrumentDatabase: true
});

startAgent();

import { NestFactory } from '@nestjs/core';
import { AppModule } from './app.module';

async function bootstrap() {
  const app = await NestFactory.create(AppModule);
  await app.listen(3000);
}

bootstrap();

// All controllers and services are automatically traced`;

      default:
        return '';
    }
  };

  const getPythonInstrumentation = () => {
    const baseUrl = 'https://api-monitoring.devskin.com';

    switch (pythonSetupType) {
      case 'flask':
        return `# 1. Install
pip install devskin-agent

# 2. Initialize Flask app
from flask import Flask, jsonify
from devskin_agent import init, start_agent
from devskin_agent.instrumentation import flask_middleware

agent = init(
    server_url='${baseUrl}',
    api_key='${apiKey}',
    application_id='${selectedApplication?.id}',
    service_name='${serviceName}',
    environment='production'
)

start_agent()

# 3. Add middleware
app = Flask(__name__)
flask_middleware(app, agent)

# Done! All routes and database queries are now automatically traced
@app.route('/api/users')
def get_users():
    # SQLAlchemy queries automatically traced
    users = db.session.query(User).filter_by(active=True).all()
    return jsonify([u.to_dict() for u in users])

if __name__ == '__main__':
    app.run()

# ✅ HTTP requests automatically traced
# ✅ SQLAlchemy queries automatically captured
# ✅ Exception tracking with stack traces`;

      case 'django':
        return `# 1. Install
pip install devskin-agent

# 2. Add to settings.py
# File: settings.py
from devskin_agent import init, start_agent

# Initialize at the top of settings.py
agent = init(
    server_url='${baseUrl}',
    api_key='${apiKey}',
    application_id='${selectedApplication?.id}',
    service_name='${serviceName}',
    environment='production'
)

start_agent()

# 3. Add middleware to MIDDLEWARE list
MIDDLEWARE = [
    'devskin_agent.instrumentation.DjangoMiddleware',  # Add this first
    'django.middleware.security.SecurityMiddleware',
    'django.contrib.sessions.middleware.SessionMiddleware',
    # ... rest of middleware
]

# 4. That's it! All views and ORM queries are now traced
# File: views.py
from django.http import JsonResponse
from .models import User

def get_users(request):
    # Django ORM queries automatically traced with:
    # - db.system = "postgresql" or "mysql"
    # - db.statement = "SELECT * FROM users WHERE active = TRUE"
    # - Duration, rows returned
    users = User.objects.filter(active=True).select_related('profile')
    return JsonResponse({'users': list(users.values())})

# ✅ HTTP requests automatically traced
# ✅ Django ORM queries automatically captured
# ✅ Template rendering tracked
# ✅ Cache operations monitored
# ✅ Exception tracking with full context`;

      case 'fastapi':
        return `# 1. Install
pip install devskin-agent

# 2. Initialize FastAPI app
# File: main.py
from fastapi import FastAPI
from devskin_agent import init, start_agent
from devskin_agent.instrumentation import fastapi_middleware

agent = init(
    server_url='${baseUrl}',
    api_key='${apiKey}',
    application_id='${selectedApplication?.id}',
    service_name='${serviceName}',
    environment='production'
)

start_agent()

# 3. Add middleware
app = FastAPI()
app.add_middleware(fastapi_middleware(agent))

# Done! All async routes and database queries are traced
@app.get("/api/users")
async def get_users():
    # SQLAlchemy async queries automatically traced
    async with async_session() as session:
        result = await session.execute(
            select(User).filter_by(active=True)
        )
        users = result.scalars().all()
    return {"users": [u.dict() for u in users]}

# ✅ Async HTTP requests automatically traced
# ✅ SQLAlchemy (sync & async) queries captured
# ✅ Background tasks monitored
# ✅ Exception tracking with async context
# ✅ WebSocket connections tracked

# Run with: uvicorn main:app --reload`;

      default:
        return '';
    }
  };

  const getJavaInstrumentation = () => {
    const baseUrl = 'https://api-monitoring.devskin.com';
    return `# OpenTelemetry Java Agent - Zero-code instrumentation
# Automatically captures HTTP requests, database queries, and more!

# 1. Download OpenTelemetry Java Agent
curl -L -o opentelemetry-javaagent.jar \\
  https://github.com/open-telemetry/opentelemetry-java-instrumentation/releases/latest/download/opentelemetry-javaagent.jar

# 2. Configure environment variables
export OTEL_SERVICE_NAME="${serviceName}"
export OTEL_RESOURCE_ATTRIBUTES="devskin.application.id=${selectedApplication?.id},environment=production"
export OTEL_EXPORTER_OTLP_ENDPOINT="${baseUrl}/api/v1/apm/otlp"
export OTEL_EXPORTER_OTLP_HEADERS="X-Devskin-API-Key=${apiKey},X-Devskin-Application-Id=${selectedApplication?.id}"
export OTEL_TRACES_EXPORTER="otlp"
export OTEL_METRICS_EXPORTER="otlp"

# Database instrumentation (captures SQL statements)
export OTEL_INSTRUMENTATION_JDBC_STATEMENT_SANITIZER_ENABLED=false  # Show full SQL
export OTEL_INSTRUMENTATION_COMMON_DB_STATEMENT_SANITIZER_ENABLED=false

# 3. Run your application
java -javaagent:opentelemetry-javaagent.jar -jar your-application.jar

# ✅ HTTP requests automatically traced
# ✅ Database queries automatically captured (JDBC, Hibernate, JPA)
# ✅ No code changes needed!

# Example: Spring Boot with JPA (works out of the box)
@RestController
public class UserController {
    @Autowired
    private UserRepository userRepository;

    @GetMapping("/api/users")
    public List<User> getUsers() {
        // Query is automatically traced with:
        // - db.system = "postgresql"
        // - db.statement = "SELECT * FROM users"
        // - db.operation = "SELECT"
        return userRepository.findAll();
    }
}

# Example: Plain JDBC (also automatic)
Connection conn = DriverManager.getConnection(url, user, password);
Statement stmt = conn.createStatement();
// Query automatically captured
ResultSet rs = stmt.executeQuery("SELECT * FROM users WHERE active = true");

# Supported databases (automatic instrumentation):
# ✅ MySQL, PostgreSQL, Oracle, SQL Server, H2
# ✅ MongoDB, Redis, Cassandra, Elasticsearch
# ✅ Hibernate, JPA, MyBatis, JOOQ

# What you'll see in Kubmix:
# 📊 /apm/database - All queries with execution time
# 📊 /apm/traces - HTTP requests with nested DB spans
# 📊 /apm/errors - Exceptions with full stack traces
# 📊 /apm/service-map - Service dependencies

# Dockerfile Example
FROM eclipse-temurin:17-jre
ADD https://github.com/open-telemetry/opentelemetry-java-instrumentation/releases/latest/download/opentelemetry-javaagent.jar /opt/otel.jar
COPY target/app.jar /app.jar

ENV OTEL_SERVICE_NAME="${serviceName}"
ENV OTEL_RESOURCE_ATTRIBUTES="devskin.application.id=${selectedApplication?.id}"
ENV OTEL_EXPORTER_OTLP_ENDPOINT="${baseUrl}/api/v1/apm/otlp"
ENV OTEL_EXPORTER_OTLP_HEADERS="X-Devskin-API-Key=${apiKey},X-Devskin-Application-Id=${selectedApplication?.id}"
ENV OTEL_INSTRUMENTATION_JDBC_STATEMENT_SANITIZER_ENABLED=false
ENV OTEL_INSTRUMENTATION_COMMON_DB_STATEMENT_SANITIZER_ENABLED=false

ENTRYPOINT ["java", "-javaagent:/opt/otel.jar", "-jar", "/app.jar"]`;
  };

  const getGoInstrumentation = () => {
    const baseUrl = 'https://api-monitoring.devskin.com';
    return `# OpenTelemetry Go - HTTP, database, and more!

# 1. Install dependencies
go get go.opentelemetry.io/otel
go get go.opentelemetry.io/otel/exporters/otlp/otlptrace/otlptracehttp
go get go.opentelemetry.io/otel/sdk/trace
go get go.opentelemetry.io/otel/sdk/resource
go get go.opentelemetry.io/otel/semconv/v1.21.0
go get go.opentelemetry.io/otel/attribute

# For database instrumentation
go get go.opentelemetry.io/contrib/instrumentation/database/sql/otelsql
go get github.com/lib/pq  # PostgreSQL
go get github.com/go-sql-driver/mysql  # MySQL

# 2. Initialize tracer (main.go)
package main

import (
    "context"
    "database/sql"
    "log"
    "go.opentelemetry.io/otel"
    "go.opentelemetry.io/otel/attribute"
    "go.opentelemetry.io/otel/exporters/otlp/otlptrace/otlptracehttp"
    "go.opentelemetry.io/otel/sdk/resource"
    sdktrace "go.opentelemetry.io/otel/sdk/trace"
    semconv "go.opentelemetry.io/otel/semconv/v1.21.0"
    "go.opentelemetry.io/contrib/instrumentation/database/sql/otelsql"
    _ "github.com/lib/pq"
)

func initTracer() func() {
    ctx := context.Background()

    exporter, _ := otlptracehttp.New(ctx,
        otlptracehttp.WithEndpoint("${baseUrl.replace('https://', '')}"),
        otlptracehttp.WithURLPath("/api/v1/apm/otlp/v1/traces"),
        otlptracehttp.WithHeaders(map[string]string{
            "X-Devskin-API-Key": "${apiKey}",
            "X-Devskin-Application-Id": "${selectedApplication?.id}",
        }),
    )

    res, _ := resource.New(ctx,
        resource.WithAttributes(
            semconv.ServiceName("${serviceName}"),
            attribute.String("devskin.application.id", "${selectedApplication?.id}"),
        ),
    )

    tp := sdktrace.NewTracerProvider(
        sdktrace.WithBatcher(exporter),
        sdktrace.WithResource(res),
    )
    otel.SetTracerProvider(tp)

    return func() { tp.Shutdown(ctx) }
}

# 3. Database instrumentation (automatic!)
func main() {
    shutdown := initTracer()
    defer shutdown()

    // Use otelsql wrapper for automatic query tracing
    db, err := otelsql.Open("postgres", "postgres://user:pass@localhost/db",
        otelsql.WithAttributes(semconv.DBSystemPostgreSQL),
    )
    if err != nil {
        log.Fatal(err)
    }
    defer db.Close()

    // Query automatically traced with db.system, db.statement, duration
    rows, err := db.Query("SELECT id, name FROM users WHERE active = $1", true)
    if err != nil {
        // Error automatically captured and sent to /apm/errors
        log.Printf("Query error: %v", err)
    }
}

# 4. Gin framework + database (full example)
go get go.opentelemetry.io/contrib/instrumentation/github.com/gin-gonic/gin/otelgin

package main

import (
    "github.com/gin-gonic/gin"
    "go.opentelemetry.io/contrib/instrumentation/github.com/gin-gonic/gin/otelgin"
)

func main() {
    shutdown := initTracer()
    defer shutdown()

    // Database with auto-instrumentation
    db, _ := otelsql.Open("postgres", "postgres://localhost/mydb")

    r := gin.Default()
    r.Use(otelgin.Middleware("${serviceName}"))

    r.GET("/api/users", func(c *gin.Context) {
        // Query automatically traced
        rows, err := db.Query("SELECT * FROM users")
        if err != nil {
            // Error captured in /apm/errors with full context
            c.JSON(500, gin.H{"error": err.Error()})
            return
        }
        // Process rows...
        c.JSON(200, users)
    })

    r.Run(":8080")
}

# What you'll see in Kubmix:
# 📊 /apm/traces - HTTP request → DB query nested span
# 📊 /apm/database - Query: "SELECT * FROM users", duration, db.system=postgres
# 📊 /apm/errors - Exceptions with stack trace + HTTP context
# 📊 /apm/service-map - Automatic service discovery

# Supported databases:
# ✅ PostgreSQL, MySQL, SQLite (via otelsql)
# ✅ MongoDB, Redis (via official contrib packages)
# ✅ GORM ORM (automatic with otelsql)
}`;
  };

  const getDotNetInstrumentation = () => {
    const baseUrl = 'https://api-monitoring.devskin.com';
    return `# Kubmix uses OpenTelemetry for .NET APM
# Auto-instrumentation for ASP.NET Core, Entity Framework, HttpClient, and more!

# Step 1: Install OpenTelemetry NuGet Packages
dotnet add package OpenTelemetry.Exporter.OpenTelemetryProtocol
dotnet add package OpenTelemetry.Extensions.Hosting
dotnet add package OpenTelemetry.Instrumentation.AspNetCore
dotnet add package OpenTelemetry.Instrumentation.Http
dotnet add package OpenTelemetry.Instrumentation.EntityFrameworkCore

# Step 2: Configure in Program.cs
using OpenTelemetry.Resources;
using OpenTelemetry.Trace;

var builder = WebApplication.CreateBuilder(args);

// Add OpenTelemetry Tracing
builder.Services.AddOpenTelemetry()
    .ConfigureResource(resource => resource
        .AddService("${serviceName}")
        .AddAttributes(new Dictionary<string, object>
        {
            ["deployment.environment"] = "production",
            ["service.version"] = "1.0.0",
            // 🔑 REQUIRED: Links traces to your application
            ["application.id"] = "${selectedApplication?.id}"
        }))
    .WithTracing(tracing => tracing
        .AddAspNetCoreInstrumentation(options =>
        {
            options.RecordException = true;
        })
        .AddHttpClientInstrumentation()
        .AddEntityFrameworkCoreInstrumentation(options =>
        {
            // Capture full SQL statements (no sanitization)
            options.SetDbStatementForText = true;
            options.SetDbStatementForStoredProcedure = true;
            // Enrich with connection details
            options.EnrichWithIDbCommand = (activity, command) =>
            {
                activity.SetTag("db.name", command.Connection?.Database);
            };
        })
        .AddOtlpExporter(options =>
        {
            options.Endpoint = new Uri("${baseUrl}/api/v1/apm/otlp/v1/traces");
            options.Headers = "X-Devskin-API-Key=${apiKey},X-Devskin-Application-Id=${selectedApplication?.id}";
        }));

var app = builder.Build();
app.MapControllers();
app.Run();

# Step 3: Configure in appsettings.json (Alternative)
{
  "OpenTelemetry": {
    "ServiceName": "${serviceName}",
    "Exporter": {
      "Otlp": {
        "Endpoint": "${baseUrl}/api/v1/apm/otlp/v1/traces",
        "Headers": "X-Devskin-API-Key=${apiKey},X-Devskin-Application-Id=${selectedApplication?.id}"
      }
    }
  }
}

# 3. Your controllers work automatically (zero code changes!)
[ApiController]
[Route("api/[controller]")]
public class UsersController : ControllerBase
{
    private readonly AppDbContext _db;

    public UsersController(AppDbContext db) => _db = db;

    [HttpGet]
    public async Task<IActionResult> GetUsers()
    {
        try {
            // Query automatically traced with:
            // - db.system = "mssql" or "postgresql"
            // - db.statement = "SELECT * FROM Users WHERE Active = 1"
            // - Duration, rows affected
            var users = await _db.Users
                .Where(u => u.Active)
                .ToListAsync();

            return Ok(users);
        }
        catch (Exception ex) {
            // Exception captured in /apm/errors with full stack trace
            throw;
        }
    }
}

# What you'll see in Kubmix:
# 📊 /apm/traces - HTTP request → EF query nested span
# 📊 /apm/database - All queries with execution time
# 📊 /apm/errors - Exceptions with stack trace + HTTP context
# 📊 /apm/service-map - Automatic service discovery

# Supported databases:
# ✅ SQL Server, PostgreSQL, MySQL, SQLite
# ✅ Entity Framework Core (automatic)
# ✅ Dapper, ADO.NET (automatic)
# ✅ MongoDB, Redis (via separate packages)
# ✅ Azure SDK, AWS SDK

# Dockerfile Example
FROM mcr.microsoft.com/dotnet/aspnet:8.0
WORKDIR /app
COPY publish/ .

ENV OTEL_SERVICE_NAME="${serviceName}"
ENV OTEL_RESOURCE_ATTRIBUTES="application.id=${selectedApplication?.id}"
ENV OTEL_EXPORTER_OTLP_ENDPOINT="${baseUrl}/api/v1/apm/otlp/v1/traces"
ENV OTEL_EXPORTER_OTLP_HEADERS="X-Devskin-API-Key=${apiKey},X-Devskin-Application-Id=${selectedApplication?.id}"

ENTRYPOINT ["dotnet", "YourApp.dll"]`;
  };

  const getPHPInstrumentation = () => {
    const baseUrl = 'https://api-monitoring.devskin.com';

    switch (phpSetupType) {
      case 'laravel':
        return `# OpenTelemetry PHP - Laravel (Manual Setup - No Extension Required)

# IMPORTANT: This setup works WITHOUT the ext-opentelemetry PECL extension
# Auto-instrumentation packages require ext-opentelemetry which is not commonly available

# 1. Install OpenTelemetry PHP via Composer
composer require open-telemetry/sdk
composer require open-telemetry/exporter-otlp

# 2. Add to .env file
OTEL_SERVICE_NAME="${serviceName}"
OTEL_EXPORTER_OTLP_ENDPOINT="${baseUrl}/api/v1/apm/otlp"
OTEL_EXPORTER_OTLP_PROTOCOL=http/json
OTEL_RESOURCE_ATTRIBUTES=application.id=${selectedApplication?.id}

# 3. Create config/opentelemetry.php
<?php

return [
    'service_name' => env('OTEL_SERVICE_NAME', '${serviceName}'),
    'exporter' => [
        'endpoint' => env('OTEL_EXPORTER_OTLP_ENDPOINT', '${baseUrl}/api/v1/apm/otlp') . '/v1/traces',
        'protocol' => 'http/json',
        'headers' => [
            'X-Devskin-API-Key' => '${apiKey}',
            'X-Devskin-Application-Id' => '${selectedApplication?.id}',
        ],
    ],
];

# 4. Create ServiceProvider: app/Providers/OpenTelemetryServiceProvider.php
<?php

namespace App\\Providers;

use Illuminate\\Support\\ServiceProvider;
use OpenTelemetry\\SDK\\Trace\\TracerProvider;
use OpenTelemetry\\SDK\\Trace\\SpanProcessor\\SimpleSpanProcessor;
use OpenTelemetry\\SDK\\Resource\\ResourceInfo;
use OpenTelemetry\\SDK\\Common\\Attribute\\Attributes;
use OpenTelemetry\\Contrib\\Otlp\\SpanExporter;
use OpenTelemetry\\API\\Trace\\TracerInterface;

class OpenTelemetryServiceProvider extends ServiceProvider
{
    public function register()
    {
        $this->app->singleton(TracerInterface::class, function ($app) {
            $config = config('opentelemetry');

            $resource = ResourceInfo::create(Attributes::create([
                'service.name' => $config['service_name'],
            ]));

            $exporter = new SpanExporter(
                $config['exporter']['endpoint'],
                $config['exporter']['headers']
            );

            $tracerProvider = new TracerProvider(
                new SimpleSpanProcessor($exporter),
                null,
                $resource
            );

            return $tracerProvider->getTracer('laravel-app');
        });
    }
}

# 5. Create Middleware: app/Http/Middleware/OpenTelemetryTrace.php
<?php

namespace App\\Http\\Middleware;

use Closure;
use OpenTelemetry\\API\\Trace\\TracerInterface;
use OpenTelemetry\\API\\Trace\\SpanKind;
use OpenTelemetry\\API\\Trace\\StatusCode;

class OpenTelemetryTrace
{
    private $tracer;

    public function __construct(TracerInterface $tracer)
    {
        $this->tracer = $tracer;
    }

    public function handle($request, Closure $next)
    {
        $span = $this->tracer->spanBuilder($request->method() . ' ' . $request->path())
            ->setSpanKind(SpanKind::KIND_SERVER)
            ->startSpan();

        $span->setAttribute('http.method', $request->method());
        $span->setAttribute('http.url', $request->fullUrl());
        $span->setAttribute('http.route', $request->route()?->getName() ?? $request->path());

        try {
            $response = $next($request);
            $span->setAttribute('http.status_code', $response->getStatusCode());

            if ($response->getStatusCode() >= 400) {
                $span->setStatus(StatusCode::STATUS_ERROR);
            }

            return $response;
        } catch (\\Throwable $e) {
            $span->recordException($e);
            $span->setStatus(StatusCode::STATUS_ERROR, $e->getMessage());
            throw $e;
        } finally {
            $span->end();
        }
    }
}

# 6. Create Query Listener: app/Providers/DatabaseQueryTracingProvider.php
<?php

namespace App\\Providers;

use Illuminate\\Support\\ServiceProvider;
use Illuminate\\Support\\Facades\\DB;
use OpenTelemetry\\API\\Trace\\TracerInterface;
use OpenTelemetry\\API\\Trace\\SpanKind;

class DatabaseQueryTracingProvider extends ServiceProvider
{
    public function boot()
    {
        DB::listen(function ($query) {
            $tracer = app(TracerInterface::class);

            $span = $tracer->spanBuilder('db.query')
                ->setSpanKind(SpanKind::KIND_CLIENT)
                ->startSpan();

            $span->setAttribute('db.system', $query->connectionName);
            $span->setAttribute('db.statement', $query->sql);
            $span->setAttribute('db.operation', $this->getOperation($query->sql));
            $span->setAttribute('db.duration_ms', $query->time);

            $span->end();
        });
    }

    private function getOperation($sql)
    {
        preg_match('/^\\s*(\\w+)/i', $sql, $matches);
        return strtoupper($matches[1] ?? 'UNKNOWN');
    }
}

# 7. Register providers in config/app.php
'providers' => [
    // ...
    App\\Providers\\OpenTelemetryServiceProvider::class,
    App\\Providers\\DatabaseQueryTracingProvider::class,
],

# 8. Register middleware in app/Http/Kernel.php
protected $middleware = [
    // ...
    \\App\\Http\\Middleware\\OpenTelemetryTrace::class,
];

# 9. Example Controller - Queries now captured!
<?php

namespace App\\Http\\Controllers;

use App\\Models\\User;

class UserController extends Controller
{
    public function index()
    {
        // Query traced with:
        // - db.system, db.statement, db.operation, duration
        $users = User::where('active', true)
            ->with('posts')
            ->get();

        return response()->json($users);
    }
}

# ✅ HTTP requests traced with full context
# ✅ Database queries (Eloquent/Query Builder) captured
# ✅ Exceptions recorded with stack traces
# ✅ Works without PECL extension!

# 📝 Optional: Install PECL extension for auto-instrumentation
# If you want full auto-instrumentation:
# pecl install opentelemetry
# Then add to php.ini: extension=opentelemetry.so
# Then install: composer require open-telemetry/opentelemetry-auto-laravel`;

      case 'symfony':
        return `# OpenTelemetry PHP - Symfony Auto-instrumentation

# 1. Install OpenTelemetry PHP via Composer
composer require open-telemetry/sdk
composer require open-telemetry/exporter-otlp
composer require open-telemetry/opentelemetry-auto-symfony
composer require open-telemetry/opentelemetry-auto-pdo

# 2. Configure OpenTelemetry
# File: config/packages/opentelemetry.yaml
opentelemetry:
    service_name: '${serviceName}'
    service_version: '1.0.0'

    exporter:
        otlp:
            endpoint: '${baseUrl}/api/v1/apm/otlp/v1/traces'
            headers:
                X-Devskin-API-Key: '${apiKey}'
                X-Devskin-Application-Id: '${selectedApplication?.id}'

    resource_attributes:
        deployment.environment: 'production'
        application.id: '${selectedApplication?.id}'

    instrumentation:
        doctrine: true     # Doctrine ORM queries
        pdo: true          # PDO database queries
        http_client: true  # Guzzle, Symfony HttpClient
        cache: true        # Redis, Memcached
        messenger: true    # Symfony Messenger

    database:
        capture_statements: true   # Capture SQL statements
        sanitize_sql: false        # Show full SQL with values

# 3. Example Controller - Doctrine auto-traced
<?php

namespace App\\Controller;

use App\\Entity\\User;
use Doctrine\\ORM\\EntityManagerInterface;
use Symfony\\Bundle\\FrameworkBundle\\Controller\\AbstractController;
use Symfony\\Component\\HttpFoundation\\JsonResponse;

class UserController extends AbstractController
{
    public function index(EntityManagerInterface $em): JsonResponse
    {
        // Query automatically traced with:
        // - db.system = "postgresql"
        // - db.statement = "SELECT * FROM users WHERE active = TRUE"
        // - db.operation = "SELECT"
        // - Duration, rows returned
        $users = $em->getRepository(User::class)
            ->findBy(['active' => true]);

        return $this->json($users);
    }
}

# ✅ HTTP requests automatically traced
# ✅ Doctrine ORM queries automatically captured
# ✅ Symfony HttpClient requests monitored
# ✅ Cache operations (Redis, Memcached) traced
# ✅ Symfony Messenger jobs tracked
# ✅ Exception tracking with full context`;

      case 'wordpress':
        return `# OpenTelemetry PHP - WordPress Manual Instrumentation

# IMPORTANT: Works WITHOUT ext-opentelemetry extension

# 1. Install OpenTelemetry PHP via Composer
# Run in your WordPress root directory
composer require open-telemetry/sdk
composer require open-telemetry/exporter-otlp

# 2. Initialize in wp-config.php
# File: wp-config.php (add at the top, after <?php)
<?php

require_once __DIR__ . '/vendor/autoload.php';

use OpenTelemetry\\SDK\\Trace\\TracerProviderBuilder;
use OpenTelemetry\\SDK\\Resource\\ResourceInfoFactory;
use OpenTelemetry\\Contrib\\Otlp\\OtlpHttpTransportFactory;
use OpenTelemetry\\Contrib\\Otlp\\SpanExporter;
use OpenTelemetry\\API\\Globals;

// Initialize OpenTelemetry
$transport = (new OtlpHttpTransportFactory())->create(
    '${baseUrl}/api/v1/apm/otlp/v1/traces',
    'application/json',
    [
        'headers' => [
            'X-Devskin-API-Key' => '${apiKey}',
            'X-Devskin-Application-Id' => '${selectedApplication?.id}',
        ]
    ]
);

$exporter = new SpanExporter($transport);
$tracerProvider = (new TracerProviderBuilder())
    ->addSpanProcessor(
        new \\OpenTelemetry\\SDK\\Trace\\SpanProcessor\\BatchSpanProcessor($exporter)
    )
    ->setResource(ResourceInfoFactory::defaultResource())
    ->build();

Globals::registerTracerProvider($tracerProvider);

// Rest of wp-config.php...
define('DB_NAME', 'your_database');
// ...

# 3. Add WordPress hooks for automatic tracing
# File: wp-content/mu-plugins/devskin-apm.php
<?php

use OpenTelemetry\\API\\Globals;

// Trace page requests
add_action('init', function() {
    $tracer = Globals::tracerProvider()->getTracer('${serviceName}');
    $span = $tracer->spanBuilder(\$_SERVER['REQUEST_URI'])->startSpan();

    register_shutdown_function(function() use ($span) {
        $span->end();
    });
});

// Trace database queries
add_filter('query', function($query) use ($tracer) {
    $span = $tracer->spanBuilder('db.query')->startSpan();
    $span->setAttribute('db.statement', $query);
    $span->setAttribute('db.system', 'mysql');

    // End span after query execution
    add_action('shutdown', function() use ($span) {
        $span->end();
    }, 1);

    return $query;
});

# ✅ HTTP requests traced (page loads)
# ✅ WordPress database queries captured
# ✅ Custom plugin hooks can be traced
# ✅ Exception tracking available`;

      case 'php-pure':
        return `# OpenTelemetry PHP - Pure PHP (No Framework)

# IMPORTANT: Works WITHOUT ext-opentelemetry extension
# Manual instrumentation for HTTP and database queries

# 1. Install OpenTelemetry PHP via Composer
composer require open-telemetry/sdk
composer require open-telemetry/exporter-otlp

# 2. Initialize tracer
# File: bootstrap.php or index.php
<?php

require 'vendor/autoload.php';

use OpenTelemetry\\SDK\\Trace\\TracerProviderBuilder;
use OpenTelemetry\\SDK\\Resource\\ResourceInfoFactory;
use OpenTelemetry\\Contrib\\Otlp\\OtlpHttpTransportFactory;
use OpenTelemetry\\Contrib\\Otlp\\SpanExporter;
use OpenTelemetry\\API\\Globals;

// Initialize OpenTelemetry
$transport = (new OtlpHttpTransportFactory())->create(
    '${baseUrl}/api/v1/apm/otlp/v1/traces',
    'application/json',
    [
        'headers' => [
            'X-Devskin-API-Key' => '${apiKey}',
            'X-Devskin-Application-Id' => '${selectedApplication?.id}',
        ]
    ]
);

$exporter = new SpanExporter($transport);

$tracerProvider = (new TracerProviderBuilder())
    ->addSpanProcessor(
        new \\OpenTelemetry\\SDK\\Trace\\SpanProcessor\\SimpleSpanProcessor($exporter)
    )
    ->setResource(ResourceInfoFactory::defaultResource())
    ->build();

Globals::registerTracerProvider($tracerProvider);

$tracer = Globals::tracerProvider()->getTracer('${serviceName}');

# 3. Manual instrumentation for HTTP requests
<?php

// Start HTTP span
$httpSpan = $tracer->spanBuilder(\$_SERVER['REQUEST_METHOD'] . ' ' . \$_SERVER['REQUEST_URI'])
    ->setSpanKind(OpenTelemetry\\API\\Trace\\SpanKind::KIND_SERVER)
    ->startSpan();

$httpSpan->setAttribute('http.method', \$_SERVER['REQUEST_METHOD']);
$httpSpan->setAttribute('http.url', \$_SERVER['REQUEST_URI']);

# 4. Manual database instrumentation with PDO
<?php

class TracedPDO extends PDO
{
    private $tracer;

    public function __construct($dsn, $username, $password, $tracer)
    {
        parent::__construct($dsn, $username, $password);
        $this->tracer = $tracer;
    }

    public function query($sql, ...$args)
    {
        $span = $this->tracer->spanBuilder('db.query')
            ->setSpanKind(OpenTelemetry\\API\\Trace\\SpanKind::KIND_CLIENT)
            ->startSpan();

        $span->setAttribute('db.system', 'mysql');
        $span->setAttribute('db.statement', $sql);

        // Extract operation (SELECT, INSERT, etc.)
        preg_match('/^\\s*(\\w+)/i', $sql, $matches);
        $span->setAttribute('db.operation', strtoupper($matches[1] ?? 'QUERY'));

        try {
            $result = parent::query($sql, ...$args);
            return $result;
        } catch (Exception $e) {
            $span->recordException($e);
            $span->setStatus(OpenTelemetry\\API\\Trace\\StatusCode::STATUS_ERROR);
            throw $e;
        } finally {
            $span->end();
        }
    }

    public function exec($sql)
    {
        $span = $this->tracer->spanBuilder('db.exec')
            ->setSpanKind(OpenTelemetry\\API\\Trace\\SpanKind::KIND_CLIENT)
            ->startSpan();

        $span->setAttribute('db.system', 'mysql');
        $span->setAttribute('db.statement', $sql);

        try {
            $result = parent::exec($sql);
            $span->setAttribute('db.rows_affected', $result);
            return $result;
        } catch (Exception $e) {
            $span->recordException($e);
            $span->setStatus(OpenTelemetry\\API\\Trace\\StatusCode::STATUS_ERROR);
            throw $e;
        } finally {
            $span->end();
        }
    }
}

// Use TracedPDO instead of regular PDO
$pdo = new TracedPDO('mysql:host=localhost;dbname=mydb', 'user', 'pass', $tracer);

// All queries now traced automatically
$stmt = $pdo->query("SELECT * FROM users WHERE active = 1");
// ✅ Traced with: db.system=mysql, db.statement=SELECT..., db.operation=SELECT

# 5. Custom spans for business logic
<?php

$businessSpan = $tracer->spanBuilder('process-order')->startSpan();
$businessSpan->setAttribute('order.id', $orderId);

try {
    // Your business logic
    $result = processOrder($orderId);
} catch (Exception $e) {
    $businessSpan->recordException($e);
    $businessSpan->setStatus(OpenTelemetry\\API\\Trace\\StatusCode::STATUS_ERROR);
    throw $e;
} finally {
    $businessSpan->end();
}

# 6. End HTTP span at the end of request
register_shutdown_function(function() use ($httpSpan) {
    $httpSpan->setAttribute('http.status_code', http_response_code());
    $httpSpan->end();
});

# ✅ HTTP requests manually traced
# ✅ PDO database queries captured via TracedPDO wrapper
# ✅ Custom spans for business logic
# ✅ Exception tracking with full context
# ✅ Works without PECL extension!`;

      default:
        return '';
    }
  };

  const getInstallCommand = () => {
    switch (selectedLanguage) {
      case 'nodejs':
        return getNodeJSInstrumentation();
      case 'python':
        return getPythonInstrumentation();
      case 'java':
        return getJavaInstrumentation();
      case 'go':
        return getGoInstrumentation();
      case 'dotnet':
        return getDotNetInstrumentation();
      case 'php':
        return getPHPInstrumentation();
      case 'ruby':
        return getRubyInstrumentation();
      default:
        return '';
    }
  };

  const getRubyInstrumentation = () => {
    const baseUrl = 'https://api-monitoring.devskin.com';
    return `# OpenTelemetry Ruby - Rails / plain Ruby

# 1. Add to your Gemfile
gem 'opentelemetry-sdk'
gem 'opentelemetry-exporter-otlp'
gem 'opentelemetry-instrumentation-all'

# 2. bundle install
bundle install

# 3. Create config/initializers/devskin_otel.rb (Rails)
#    OR require it once in your Ruby entry point (plain Ruby)
require 'opentelemetry/sdk'
require 'opentelemetry/exporter/otlp'
require 'opentelemetry/instrumentation/all'

OpenTelemetry::SDK.configure do |c|
  c.service_name = '${serviceName}'
  c.resource = OpenTelemetry::SDK::Resources::Resource.create(
    'devskin.application.id' => '${selectedApplication?.id}'
  )
  c.use_all  # auto-instruments Rails, Sidekiq, Net::HTTP, ActiveRecord, …
  c.add_span_processor(
    OpenTelemetry::SDK::Trace::Export::BatchSpanProcessor.new(
      OpenTelemetry::Exporter::OTLP::Exporter.new(
        endpoint: '${baseUrl}/api/v1/apm/otlp/v1/traces',
        headers: {
          'X-Devskin-API-Key' => '${apiKey}',
          'X-Devskin-Application-Id' => '${selectedApplication?.id}'
        }
      )
    )
  )
end

# 4. Boot Rails as usual — controllers and ActiveRecord queries are
#    automatically traced. No code changes in your business logic.

# Example: Rails controller (no changes needed)
class UsersController < ApplicationController
  def index
    # Auto-instrumented:
    #   db.system = "postgresql"
    #   db.statement = "SELECT \\"users\\".* FROM \\"users\\""
    @users = User.where(active: true)
    render json: @users
  end
end

# What you'll see in Kubmix:
# 📊 /apm/traces   — HTTP request → ActiveRecord query nested span
# 📊 /apm/database — All ActiveRecord queries with execution time
# 📊 /apm/errors   — Exceptions with full backtrace
# 📊 /apm/service-map — Service dependencies

# Supported instrumentations (auto via opentelemetry-instrumentation-all):
# ✅ Rails / Sinatra / Rack
# ✅ ActiveRecord (Postgres, MySQL, SQLite)
# ✅ Sidekiq, Resque, DelayedJob
# ✅ Net::HTTP, Faraday, REST clients
# ✅ Redis, Mongo

# Dockerfile
FROM ruby:3.3
WORKDIR /app
COPY Gemfile Gemfile.lock ./
RUN bundle install
COPY . .
ENV OTEL_SERVICE_NAME="${serviceName}"
ENV OTEL_EXPORTER_OTLP_ENDPOINT="${baseUrl}/api/v1/apm/otlp"
ENV OTEL_EXPORTER_OTLP_HEADERS="X-Devskin-API-Key=${apiKey},X-Devskin-Application-Id=${selectedApplication?.id}"
CMD ["bundle", "exec", "rails", "server"]
`;
  };

  // Show elegant empty state when All Applications is selected
  if (isAllAppsSelected) {
    return (
      <MainLayout>
        <ApplicationRequired
          title={t('warnings.applicationRequired.title')}
          message={t('warnings.applicationRequired.installPageDescription') || 'APM Agent requires a specific application to be selected. Please choose an application from the top menu to continue with the installation.'}
        />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/agents')}
            className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
              <span>📊</span>
              <span>{t('apm.title')}</span>
            </h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {t('apm.subtitle')}
            </p>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            {[
              { num: 1, label: t('install.steps.selectApiKey') },
              { num: 2, label: t('install.steps.instrumentApp') },
              { num: 3, label: t('install.steps.verifyConnection') },
              { num: 4, label: t('install.steps.complete') },
            ].map((step, idx) => (
              <div key={step.num} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                      currentStep >= step.num
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    {currentStep > step.num ? '✓' : step.num}
                  </div>
                  <span className="mt-2 text-xs font-medium text-gray-700 dark:text-gray-300">
                    {step.label}
                  </span>
                </div>
                {idx < 3 && (
                  <div
                    className={`h-1 flex-1 mx-4 ${
                      currentStep > step.num
                        ? 'bg-indigo-600'
                        : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Warning: Must Select Application */}
        {currentStep === 1 && !selectedApplication && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-2 border-amber-300 dark:border-amber-700 rounded-xl p-6">
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0">
                <svg className="w-10 h-10 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-amber-900 dark:text-amber-300 mb-2">
                  {t('install.validation.title')}
                </h3>
                <p className="text-amber-800 dark:text-amber-400 mb-3">
                  {t('install.validation.description')}
                </p>
                <div className="bg-white dark:bg-gray-800/50 rounded-lg p-4 border-l-4 border-amber-500">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                    {t('install.validation.whyRequired')}
                  </p>
                  <ul className="space-y-1 text-sm text-gray-700 dark:text-gray-300">
                    <li>• {t('install.validation.reason1')}</li>
                    <li>• {t('install.validation.reason2')}</li>
                    <li>• {t('install.validation.reason3')}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 1: Select or Generate API Key */}
        {currentStep === 1 && (
          <>
            {/* Features */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-300 mb-3 flex items-center space-x-2">
                <span>✨</span>
                <span>{t('apm.features.title')}</span>
              </h3>
              <div className="grid grid-cols-2 gap-3 text-sm text-blue-800 dark:text-blue-400">
                <div>• {t('apm.features.items.tracing')}</div>
                <div>• {t('apm.features.items.auto')}</div>
                <div>• {t('apm.features.items.metrics')}</div>
                <div>• {t('apm.features.items.errors')}</div>
                <div>• {t('apm.features.items.database')}</div>
                <div>• {t('apm.features.items.api')}</div>
                <div>• {t('apm.features.items.map')}</div>
                <div>• {t('apm.features.items.custom')}</div>
              </div>
            </div>

            {/* Existing Applications */}
            {loadingApplications ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
                <p className="mt-4 text-gray-600 dark:text-gray-400">{t('install.verification.checking')}...</p>
              </div>
            ) : existingApplications.length > 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  {t('install.steps.selectApiKey')}: {t('install.apiKey.selectExisting')}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  {t('install.apiKey.selectExistingDesc', { count: existingApplications.length })}
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {existingApplications.map((app) => (
                    <button
                      key={app.id}
                      onClick={() => handleSelectApplication(app)}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        selectedApplication?.id === app.id
                          ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-gray-900 dark:text-white">{app.name}</h3>
                        {selectedApplication?.id === app.id && (
                          <svg className="w-5 h-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{app.description}</p>
                      <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
                        <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded">
                          {app.language}
                        </span>
                        {app.framework && (
                          <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded">
                            {app.framework}
                          </span>
                        )}
                        <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded">
                          {app.environment}
                        </span>
                      </div>
                      <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
                        Created: {new Date(app.created_at).toLocaleDateString()}
                      </p>
                    </button>
                  ))}
                </div>
                {selectedApplication && (
                  <button
                    onClick={handleUseSelectedKey}
                    className="w-full px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg"
                  >
                    {t('install.apiKey.useSelected')}
                  </button>
                )}
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    {t('install.apiKey.orGenerateNew')}
                  </p>
                </div>
              </div>
            ) : null}

            {/* Generate New API Key */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              {existingApplications.length === 0 && !loadingApplications && (
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  {t('install.steps.selectApiKey')}: {t('install.apiKey.generateNew')}
                </h2>
              )}
              {existingApplications.length > 0 && (
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  {t('install.apiKey.generateNew')}
                </h3>
              )}

              {/* Language Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  {t('apm.languages.title')}
                </label>
                <div className="grid grid-cols-6 gap-3">
                  {[
                    { id: 'nodejs', label: t('apm.languages.nodejs'), icon: '🟢' },
                    { id: 'python', label: t('apm.languages.python'), icon: '🐍' },
                    { id: 'java', label: t('apm.languages.java'), icon: '☕' },
                    { id: 'go', label: t('apm.languages.go'), icon: '🐹' },
                    { id: 'dotnet', label: t('apm.languages.dotnet'), icon: '🔷' },
                    { id: 'php', label: t('apm.languages.php'), icon: '🐘' },
                    { id: 'ruby', label: t('apm.languages.ruby'), icon: '💎' },
                  ].map((lang) => (
                    <button
                      key={lang.id}
                      onClick={() => setSelectedLanguage(lang.id as Language)}
                      className={`p-4 rounded-lg border-2 text-center transition-colors ${
                        selectedLanguage === lang.id
                          ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className="text-3xl mb-2">{lang.icon}</div>
                      <div className="text-sm font-semibold text-gray-900 dark:text-white">{lang.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Service Name Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('apm.serviceName.label')}
                </label>
                <input
                  type="text"
                  value={serviceName}
                  onChange={(e) => setServiceName(e.target.value)}
                  placeholder={t('apm.serviceName.placeholder')}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {t('apm.serviceName.help')}
                </p>
              </div>

              <button
                onClick={generateApiKey}
                disabled={generatingKey || !serviceName.trim()}
                className="w-full px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {generatingKey ? t('install.apiKey.generating') : t('install.apiKey.generateNew')}
              </button>
            </div>
          </>
        )}

        {/* Step 2: Instrument Application */}
        {currentStep === 2 && (
          <>
            {/* API Key Display */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                {t('install.apiKey.title')}
              </h2>
              <div className="flex items-center space-x-2 mb-4">
                <code className="flex-1 px-4 py-3 bg-gray-100 dark:bg-gray-900 rounded-lg text-sm font-mono text-gray-800 dark:text-gray-200 break-all">
                  {apiKey}
                </code>
                <button
                  onClick={() => copyToClipboard(apiKey, 'apiKey')}
                  className={`flex items-center px-3 py-1.5 text-sm rounded-md transition-all ${
                    copiedField === 'apiKey'
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : 'text-indigo-600 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-900/20'
                  }`}
                  title={t('install.copy.apiKey')}
                >
                  {copiedField === 'apiKey' ? (
                    <>
                      <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {t('install.copy.copied')}
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      {t('install.copy.copy')}
                    </>
                  )}
                </button>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('apm.serviceName.label')}: <strong className="text-gray-900 dark:text-white">{serviceName}</strong>
              </p>
              <p className="mt-1 text-sm text-yellow-600 dark:text-yellow-500">
                {t('install.apiKey.warning')}
              </p>
            </div>

            {/* Language Selection */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                {t('apm.languages.step2Title')}
              </h2>
              <div className="grid grid-cols-7 gap-3 mb-6">
                {[
                  { id: 'nodejs', label: 'Node.js', icon: '🟢' },
                  { id: 'python', label: 'Python', icon: '🐍' },
                  { id: 'java', label: 'Java', icon: '☕' },
                  { id: 'go', label: 'Go', icon: '🐹' },
                  { id: 'dotnet', label: '.NET', icon: '🔷' },
                  { id: 'php', label: 'PHP', icon: '🐘' },
                  { id: 'ruby', label: 'Ruby', icon: '💎' },
                ].map((lang) => (
                  <button
                    key={lang.id}
                    onClick={() => setSelectedLanguage(lang.id as Language)}
                    className={`p-4 rounded-lg border-2 text-center transition-colors ${
                      selectedLanguage === lang.id
                        ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <div className="text-3xl mb-2">{lang.icon}</div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">{lang.label}</div>
                  </button>
                ))}
              </div>

              {/* Setup Type Selection (Node.js only) */}
              {selectedLanguage === 'nodejs' && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    {t('apm.setupType.title')}
                  </label>
                  <div className="grid grid-cols-5 gap-3">
                    {[
                      { id: 'express-basic', key: 'expressBasic' },
                      { id: 'express-prisma-auto', key: 'expressPrismaAuto' },
                      { id: 'express-prisma-manual', key: 'expressPrismaManual' },
                      { id: 'express-typeorm', key: 'expressTypeorm' },
                      { id: 'nestjs', key: 'nestjs' },
                    ].map((type) => (
                      <button
                        key={type.id}
                        onClick={() => setNodejsSetupType(type.id as NodeJSSetupType)}
                        className={`p-4 rounded-lg border-2 text-left transition-colors ${
                          nodejsSetupType === type.id
                            ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                      >
                        <div className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                          {t(`apm.setupType.${type.key}.label`)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {t(`apm.setupType.${type.key}.desc`)}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Setup Type Selection (Python only) */}
              {selectedLanguage === 'python' && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    {t('apm.pythonSetupType.title')}
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { id: 'flask', key: 'flask' },
                      { id: 'django', key: 'django' },
                      { id: 'fastapi', key: 'fastapi' },
                    ].map((type) => (
                      <button
                        key={type.id}
                        onClick={() => setPythonSetupType(type.id as PythonSetupType)}
                        className={`p-4 rounded-lg border-2 text-left transition-colors ${
                          pythonSetupType === type.id
                            ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                      >
                        <div className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                          {t(`apm.pythonSetupType.${type.key}.label`)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {t(`apm.pythonSetupType.${type.key}.desc`)}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Setup Type Selection (PHP only) */}
              {selectedLanguage === 'php' && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    {t('apm.phpSetupType.title')}
                  </label>
                  <div className="grid grid-cols-4 gap-3">
                    {[
                      { id: 'laravel', key: 'laravel' },
                      { id: 'symfony', key: 'symfony' },
                      { id: 'wordpress', key: 'wordpress' },
                      { id: 'php-pure', key: 'phpPure' },
                    ].map((type) => (
                      <button
                        key={type.id}
                        onClick={() => setPhpSetupType(type.id as PHPSetupType)}
                        className={`p-4 rounded-lg border-2 text-left transition-colors ${
                          phpSetupType === type.id
                            ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                      >
                        <div className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                          {t(`apm.phpSetupType.${type.key}.label`)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {t(`apm.phpSetupType.${type.key}.desc`)}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Installation Instructions */}
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Installation & Configuration
              </h3>
              <div className="relative">
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm max-h-[500px]">
                  <code>{getInstallCommand()}</code>
                </pre>
                <button
                  onClick={() => copyToClipboard(getInstallCommand(), 'installCommand')}
                  className={`absolute top-2 right-2 px-3 py-1.5 text-sm rounded transition-all ${
                    copiedField === 'installCommand'
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-700 hover:bg-gray-600 text-white'
                  }`}
                >
                  {copiedField === 'installCommand' ? '✓ Copied!' : 'Copy'}
                </button>
              </div>

              {/* Important Notes */}
              <div className="mt-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-yellow-900 dark:text-yellow-300 mb-2">
                  📌 {t('apm.install.nextSteps.title')}:
                </h4>
                <ul className="space-y-1 text-sm text-yellow-800 dark:text-yellow-400 list-disc list-inside">
                  <li>{t('apm.install.nextSteps.copyCode')}</li>
                  <li>{t('apm.install.nextSteps.restart')}</li>
                  <li>{t('apm.install.nextSteps.makeRequests')}</li>
                  <li>{t('apm.install.nextSteps.verify')}</li>
                </ul>
              </div>

              <div className="mt-6 flex items-center justify-between">
                <button
                  onClick={() => setCurrentStep(1)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                >
                  {t('install.back')}
                </button>
                <button
                  onClick={() => setCurrentStep(3)}
                  className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg"
                >
                  {t('install.continue')}
                </button>
              </div>
            </div>
          </>
        )}

        {/* Step 3: Verify Traces */}
        {currentStep === 3 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              {t('install.steps.step')} 3: {t('apm.verification.title')}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t('apm.verification.description')}
            </p>

            {/* Verification Status - Enhanced UI */}
            <div className="bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-lg p-6 border-2 border-indigo-200 dark:border-indigo-800 mb-6">
              <div className="flex items-start space-x-4">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                    verification.checking
                      ? 'bg-indigo-100 dark:bg-indigo-900/40'
                      : verification.success
                      ? 'bg-green-100 dark:bg-green-900/40'
                      : 'bg-yellow-100 dark:bg-yellow-900/40'
                  }`}
                >
                  {verification.checking ? (
                    <svg className="w-6 h-6 text-indigo-600 dark:text-indigo-400 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : verification.success ? (
                    <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                    {verification.checking
                      ? t('apm.verification.checkingConnection')
                      : verification.success
                      ? t('apm.verification.tracesDetected')
                      : t('apm.verification.noTracesYet')}
                  </h3>
                  <p
                    className={`text-sm ${
                      verification.success
                        ? 'text-green-700 dark:text-green-300'
                        : verification.checking
                        ? 'text-indigo-700 dark:text-indigo-300'
                        : 'text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    {verification.message}
                  </p>
                  {verification.lastCheck && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      {t('apm.verification.lastChecked')} {verification.lastCheck.toLocaleTimeString()}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Test Checklist */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
              <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-2">
                {t('apm.verification.checklist.title')}
              </h4>
              <ul className="space-y-1 text-sm text-blue-800 dark:text-blue-400 list-disc list-inside">
                <li>{t('apm.verification.checklist.items.restart')}</li>
                <li>{t('apm.verification.checklist.items.requests')}</li>
                <li>{t('apm.verification.checklist.items.noErrors')}</li>
                <li>
                  {t('apm.verification.checklist.items.serviceName')}{' '}
                  <code className="bg-blue-100 dark:bg-blue-900/50 px-1 rounded">{serviceName}</code>
                </li>
              </ul>
            </div>

            <div className="flex items-center justify-between">
              <button
                onClick={() => setCurrentStep(2)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              >
                {t('install.back')}
              </button>
              <button
                onClick={verifyConnection}
                disabled={verification.checking}
                className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {verification.checking ? t('apm.verification.checking') : t('apm.verification.button')}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Complete */}
        {currentStep === 4 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="text-center py-8">
              <div className="text-6xl mb-4">🎉</div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {t('apm.complete.title')}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                {t('apm.complete.message')}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
                {t('apm.complete.serviceName', { name: serviceName })}
              </p>
              <div className="flex items-center justify-center space-x-4">
                <button
                  onClick={() => navigate('/apm/traces')}
                  className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg"
                >
                  {t('apm.complete.viewTraces')}
                </button>
                <button
                  onClick={() => navigate('/agents')}
                  className="px-6 py-3 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-medium rounded-lg"
                >
                  {t('apm.complete.backToAgents')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
